import { useState } from "react";
import { variantLabelOf } from "../../lib/variants";
import {
    STATUS_LABELS,
    STATUS_COLORS,
    PAYMENT_STATUS_LABELS,
    PAYMENT_STATUS_COLORS,
    paymentMethodLabel,
} from "../../lib/orderLabels";

export default function OwnerOrderCard({ order: o, onStatus, onSync }) {
    const [open, setOpen] = useState(false);
    const pay = o.payment_status || "pending";
    const waPhone = (o.phone || "").replace(/\D/g, "").replace(/^0/, "966");

    return (
        <div data-testid={`owner-order-${o.order_number}`} className={`surface-card p-4 ${pay === "paid" ? "" : "opacity-60"}`}>
            {pay !== "paid" && (
                <div className="mb-2 text-xs font-bold text-amber-800 bg-amber-50 rounded-lg px-2 py-1" data-testid={`unpaid-warning-${o.order_number}`}>
                    <i className="fa-solid fa-triangle-exclamation ms-1" /> لم يتم الدفع — لا تبدأ التحضير
                </div>
            )}
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="text-brand font-black text-lg">#{o.order_number}</div>
                    <div className="text-text-muted text-xs">{new Date(o.created_at).toLocaleString("ar-SA")}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                    <span data-testid={`order-status-${o.order_number}`} className={`px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_COLORS[o.status] || ""}`}>
                        {STATUS_LABELS[o.status] || o.status}
                    </span>
                    <span data-testid={`payment-status-${o.order_number}`} className={`px-2.5 py-1 rounded-full text-xs font-bold ${PAYMENT_STATUS_COLORS[pay]}`}>
                        <i className="fa-solid fa-credit-card ms-1" /> {PAYMENT_STATUS_LABELS[pay]}
                    </span>
                </div>
            </div>

            <div className="mt-3 text-sm space-y-0.5 text-text-main">
                <div>
                    <span className="text-text-muted">العميل:</span> <span className="font-bold">{o.customer_name}</span> -{" "}
                    <span dir="ltr">{o.phone}</span>
                </div>
                <div>
                    <span className="text-text-muted">النوع:</span>{" "}
                    <span className="font-bold">
                        <i className={`fa-solid ${o.order_type === "car" ? "fa-car" : "fa-location-dot"} text-brand ms-1`} />
                        {o.order_type === "car" ? "توصيل للسيارة" : "توصيل للموقع"}
                    </span>
                </div>
                <div className="flex justify-between font-extrabold pt-1">
                    <span>الإجمالي ({o.items.length} صنف)</span>
                    <span className="text-brand" data-testid={`order-total-${o.order_number}`}>{o.total.toFixed(2)} ر.س</span>
                </div>
            </div>

            <button
                onClick={() => setOpen(!open)}
                data-testid={`order-toggle-details-${o.order_number}`}
                className="mt-2 text-sm font-bold text-brand"
            >
                <i className={`fa-solid ${open ? "fa-chevron-up" : "fa-chevron-down"} ms-1`} />
                {open ? "إخفاء التفاصيل" : "عرض التفاصيل"}
            </button>

            {open && <OrderDetails o={o} waPhone={waPhone} onSync={onSync} />}

            <div className="mt-3 flex flex-wrap gap-1.5">
                {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => onStatus(o.id, key)}
                        data-testid={`owner-status-${o.order_number}-${key}`}
                        disabled={o.status === key}
                        className={`text-xs px-2.5 py-1 rounded-full font-bold border transition ${
                            o.status === key
                                ? "bg-brand text-white border-brand"
                                : "bg-white text-text-main border-[var(--border-subtle)] hover:border-brand"
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function OrderDetails({ o, waPhone, onSync }) {
    return (
        <div className="mt-3 border-t border-[var(--border-subtle)] pt-3 text-sm space-y-2" data-testid={`order-details-${o.order_number}`}>
            {o.order_type === "car" && o.car_info && (
                <div className="text-text-main">
                    <span className="text-text-muted">السيارة:</span> {o.car_info.car_type} - {o.car_info.car_color} - لوحة{" "}
                    <span className="font-bold">{o.car_info.plate_number}</span>
                </div>
            )}
            {o.order_type === "location" && o.location_info && (
                <div className="text-text-main space-y-0.5">
                    <div><span className="text-text-muted">العنوان:</span> {o.location_info.address}</div>
                    {o.location_info.location_link && (
                        <a href={o.location_info.location_link} target="_blank" rel="noopener noreferrer" className="text-brand font-bold" data-testid={`order-location-link-${o.order_number}`}>
                            <i className="fa-solid fa-map-location-dot ms-1" /> فتح الموقع
                        </a>
                    )}
                </div>
            )}
            <div className="space-y-1">
                {o.items.map((it, idx) => (
                    <div key={idx} className="flex justify-between">
                        <span>
                            {it.name}
                            {it.variant && <span className="text-text-muted text-xs ms-1">({variantLabelOf(it.category, it.variant)})</span>} × {it.quantity}
                            {it.notes && <span className="block text-text-muted text-xs">ملاحظة: {it.notes}</span>}
                        </span>
                        <span className="font-bold">{(it.price * it.quantity).toFixed(2)}</span>
                    </div>
                ))}
            </div>
            {o.notes && <div><span className="text-text-muted">ملاحظات:</span> {o.notes}</div>}
            <div className="text-text-muted text-xs">
                طريقة الدفع: {paymentMethodLabel(o.payment_method)}
                {o.payment_id && <span className="block" dir="ltr">ID: {o.payment_id}</span>}
                {o.paid_at && <span className="block">وقت الدفع: {new Date(o.paid_at).toLocaleString("ar-SA")}</span>}
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
                <a href={`tel:${o.phone}`} className="btn-outline-brand text-xs px-3 py-1.5" data-testid={`order-call-${o.order_number}`}>
                    <i className="fa-solid fa-phone ms-1" /> اتصال
                </a>
                <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noopener noreferrer" className="btn-outline-brand text-xs px-3 py-1.5" data-testid={`order-wa-${o.order_number}`}>
                    <i className="fa-brands fa-whatsapp ms-1" /> واتساب العميل
                </a>
                {o.payment_id && (
                    <button onClick={() => onSync(o.id)} className="btn-outline-brand text-xs px-3 py-1.5" data-testid={`order-sync-payment-${o.order_number}`}>
                        <i className="fa-solid fa-rotate ms-1" /> تحديث حالة الدفع
                    </button>
                )}
            </div>
        </div>
    );
}
